import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { Plus, Pencil, Trash2, X, CheckSquare, MapPin, Check, Building2, Layers, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import OnizlemeModal from '../components/OnizlemeModal';

function Modal({ title, onClose, wide, children }) {
  return (
    <div className="modal-overlay">
      <div className={`modal ${wide ? 'modal-lg' : ''}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const SORU_TIPLERI = [
  { value: 'evet_hayir',    label: '✅ Evet/Hayır' },
  { value: 'cok_secenekli', label: '☑️ Çoktan Seçmeli' },
  { value: 'puan',          label: '⭐ Puan (1-5)' },
  { value: 'acik_metin',   label: '📝 Açık Metin' },
];
const tipiLabel = t => SORU_TIPLERI.find(x => x.value === t)?.label || t;
const BOSH_SORU = { soru_metni: '', soru_tipi: 'evet_hayir', zorunlu: true, secenekler: [] };

function SecenekEditor({ secenekler, onChange }) {
  const refs = useRef([]);
  const add = () => { const y=[...secenekler,'']; onChange(y); setTimeout(()=>refs.current[y.length-1]?.focus(),30); };
  const upd = (i,v) => onChange(secenekler.map((s,j)=>j===i?v:s));
  const del = (i) => onChange(secenekler.filter((_,j)=>j!==i));
  const onKey = (e,i) => { if(e.key==='Enter'){e.preventDefault();e.stopPropagation(); i===secenekler.length-1?add():refs.current[i+1]?.focus();} };
  return (
    <div style={{marginTop:8}}>
      <div style={{fontSize:12,fontWeight:600,color:'var(--gray-600)',marginBottom:6}}>Seçenekler</div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {secenekler.map((s,i)=>(
          <div key={i} style={{display:'flex',gap:6,alignItems:'center'}}>
            <span style={{fontSize:12,color:'var(--gray-400)',minWidth:18}}>{i+1}.</span>
            <input ref={el=>refs.current[i]=el} className="input" style={{flex:1,padding:'7px 10px',fontSize:13}}
              placeholder={`Seçenek ${i+1}...`} value={s} onChange={e=>upd(i,e.target.value)} onKeyDown={e=>onKey(e,i)}/>
            <button type="button" className="btn btn-danger btn-icon btn-sm" onClick={()=>del(i)} tabIndex={-1}><X size={12}/></button>
          </div>
        ))}
        {!secenekler.length&&<p style={{fontSize:12,color:'var(--gray-400)',margin:0}}>Henüz seçenek yok</p>}
      </div>
      <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={add}><Plus size={12}/> Seçenek Ekle</button>
    </div>
  );
}

function SoruSatir({s,i,onSil,onEdit}) {
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:'var(--gray-50)',borderRadius:10,border:'1px solid var(--gray-100)',marginBottom:6}}>
      <span style={{fontSize:12,fontWeight:700,color:'var(--gray-400)',minWidth:20,paddingTop:2}}>{i+1}</span>
      <div style={{flex:1}}>
        <div style={{fontSize:13,fontWeight:500}}>{s.soru_metni}</div>
        <div style={{fontSize:11,color:'var(--gray-400)',marginTop:2}}>
          {tipiLabel(s.soru_tipi)} {s.zorunlu?'· Zorunlu':''}
          {s.soru_tipi==='cok_secenekli'&&s.secenekler?.length>0&&
            <span style={{color:'var(--blue)',marginLeft:6}}>({s.secenekler.slice(0,3).join(', ')}{s.secenekler.length>3?'…':''})</span>}
        </div>
      </div>
      <div style={{display:'flex',gap:4}}>
        {onEdit&&<button className="btn btn-secondary btn-sm btn-icon" type="button" onClick={()=>onEdit(s,i)}><Pencil size={12}/></button>}
        {onSil&&<button className="btn btn-danger btn-sm btn-icon" type="button" onClick={()=>onSil(s,i)}><Trash2 size={12}/></button>}
      </div>
    </div>
  );
}

function SoruForm({value,onChange,onSubmit,loading,label='Madde Ekle',onCancel}) {
  const ref = useRef(null);
  useEffect(()=>{ref.current?.focus();},[]);
  const set = (k,v) => onChange({...value,[k]:v});
  return (
    <div style={{background:'var(--gray-50)',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:10}}>
      <div>
        <label style={{fontSize:12,fontWeight:600,color:'var(--gray-600)',display:'block',marginBottom:4}}>Madde Metni *</label>
        <input ref={ref} className="input" placeholder="Kontrol maddesini yazın..." value={value.soru_metni}
          onChange={e=>set('soru_metni',e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'&&value.soru_tipi!=='cok_secenekli'){e.preventDefault();onSubmit();}}}/>
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        <select className="input" style={{flex:1,minWidth:180}} value={value.soru_tipi} onChange={e=>set('soru_tipi',e.target.value)}>
          {SORU_TIPLERI.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
          <input type="checkbox" checked={value.zorunlu} onChange={e=>set('zorunlu',e.target.checked)}/> Zorunlu
        </label>
      </div>
      {value.soru_tipi==='cok_secenekli'&&<SecenekEditor secenekler={value.secenekler||[]} onChange={v=>set('secenekler',v)}/>}
      <div style={{display:'flex',gap:8}}>
        {onCancel&&<button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>İptal</button>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={onSubmit} disabled={loading} style={{flex:1}}>
          <Plus size={14}/> {label}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   LOKASYON ATA MODAL
══════════════════════════════════════════════════════════════ */
function LokasyonAtaModal({ sablon, onClose, onAtandi }) {
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secili, setSecili] = useState(new Set());
  const [acikKatlar, setAcikKatlar] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/checklistler/${sablon.id}/atanabilir-lokasyonlar`);
        setLokasyonlar(data);
        const katlar = {};
        data.forEach(l => { const k = (l.bina_adi||'__') + '|' + (l.kat_adi||'__'); katlar[k] = true; });
        setAcikKatlar(katlar);
      } catch { toast.error('Lokasyonlar yüklenemedi'); }
      finally { setLoading(false); }
    };
    load();
  }, [sablon.id]);

  // Bina → Kat → Lokasyon grupla
  const groups = {};
  lokasyonlar.forEach(l => {
    const bina = l.bina_adi || '(Bina yok)';
    const kat  = l.kat_adi  || '__';
    if (!groups[bina]) groups[bina] = {};
    if (!groups[bina][kat]) groups[bina][kat] = [];
    groups[bina][kat].push(l);
  });

  const toggleLok = (id) => setSecili(prev => { const s=new Set(prev); s.has(id)?s.delete(id):s.add(id); return s; });
  const toggleKat = (bina, kat) => {
    const loks = groups[bina][kat]||[];
    const hepsi = loks.every(l=>secili.has(l.id));
    setSecili(prev => { const s=new Set(prev); loks.forEach(l=>hepsi?s.delete(l.id):s.add(l.id)); return s; });
  };
  const tumunuSec = () => {
    if (secili.size===lokasyonlar.length) setSecili(new Set());
    else setSecili(new Set(lokasyonlar.map(l=>l.id)));
  };
  const toggleAcikKat = (key) => setAcikKatlar(prev=>({...prev,[key]:!prev[key]}));

  const handleAta = async () => {
    if (!secili.size) return toast.error('En az bir lokasyon seçin');
    setSaving(true);
    try {
      await api.post(`/checklistler/${sablon.id}/toplu-ata`, { lokasyon_ids: [...secili] });
      toast.success(`${secili.size} lokasyona atandı`);
      onAtandi(); onClose();
    } catch (err) { toast.error(err.response?.data?.error||'Hata'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">
              <MapPin size={14} style={{marginRight:6,verticalAlign:'middle',color:'#10b981'}}/>
              Lokasyona Ata: <span style={{color:'#10b981'}}>{sablon.baslik}</span>
            </div>
            <div style={{fontSize:11,color:'var(--gray-400)',marginTop:2}}>
              Daha önce checklist atanmış lokasyonlar listede görünmez
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{display:'flex',justifyContent:'center',padding:40}}>
              <div className="spinner spinner-blue" style={{width:28,height:28}}/>
            </div>
          ) : lokasyonlar.length===0 ? (
            <div style={{textAlign:'center',padding:'40px 20px',color:'var(--gray-400)'}}>
              <MapPin size={32} style={{marginBottom:10,opacity:0.3}}/>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Atanabilir lokasyon yok</div>
              <div style={{fontSize:12}}>Tüm aktif lokasyonlara bu checklist zaten atanmış.</div>
            </div>
          ) : (
            <>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,padding:'8px 12px',background:'var(--gray-50)',borderRadius:10}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:500}}>
                  <input type="checkbox"
                    checked={secili.size===lokasyonlar.length&&lokasyonlar.length>0}
                    onChange={tumunuSec}/>
                  Tümünü Seç ({lokasyonlar.length} lokasyon)
                </label>
                <span className="badge badge-green">{secili.size} seçili</span>
              </div>

              <div style={{maxHeight:400,overflowY:'auto',display:'flex',flexDirection:'column',gap:12}}>
                {Object.entries(groups).map(([bina, katGruplari]) => (
                  <div key={bina}>
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 4px',marginBottom:6}}>
                      <Building2 size={13} color="#10b981"/>
                      <span style={{fontWeight:700,fontSize:13}}>{bina}</span>
                      <span className="badge badge-gray" style={{fontSize:10}}>
                        {Object.values(katGruplari).flat().length}
                      </span>
                    </div>

                    {Object.entries(katGruplari).map(([katKey, loks]) => {
                      const katAdi = katKey==='__' ? null : katKey;
                      const acikKey = bina+'|'+katKey;
                      const acik = acikKatlar[acikKey]!==false;
                      const hepsiSecili = loks.every(l=>secili.has(l.id));
                      const bazıSecili = loks.some(l=>secili.has(l.id))&&!hepsiSecili;

                      return (
                        <div key={katKey} style={{marginLeft:12,marginBottom:6}}>
                          {katAdi && (
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 8px',background:'#f0fdf4',borderRadius:8,marginBottom:4,cursor:'pointer'}}
                              onClick={()=>toggleAcikKat(acikKey)}>
                              <input type="checkbox"
                                checked={hepsiSecili}
                                ref={el=>{if(el) el.indeterminate=bazıSecili;}}
                                onChange={()=>toggleKat(bina,katKey)}
                                onClick={e=>e.stopPropagation()}/>
                              <Layers size={12} color="#10b981"/>
                              <span style={{fontSize:12,fontWeight:600,color:'#10b981',flex:1}}>{katAdi}</span>
                              <span className="badge badge-gray" style={{fontSize:10}}>{loks.length}</span>
                              {acik?<ChevronDown size={12}/>:<ChevronRight size={12}/>}
                            </div>
                          )}

                          {(!katAdi||acik) && (
                            <div style={{display:'flex',flexDirection:'column',gap:4,marginLeft:katAdi?8:0}}>
                              {loks.map(l=>(
                                <label key={l.id}
                                  style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',
                                    background:secili.has(l.id)?'#f0fdf4':'#fff',
                                    border:`1px solid ${secili.has(l.id)?'#bbf7d0':'var(--gray-100)'}`,
                                    borderRadius:8,cursor:'pointer',transition:'all 0.1s'}}>
                                  <input type="checkbox" checked={secili.has(l.id)} onChange={()=>toggleLok(l.id)}/>
                                  <MapPin size={11} color={secili.has(l.id)?'#10b981':'var(--gray-400)'}/>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:13,fontWeight:500}}>{l.ad}</div>
                                    {l.alan&&<div style={{fontSize:11,color:'var(--gray-400)'}}>{l.alan}</div>}
                                  </div>
                                  {secili.has(l.id)&&<Check size={14} color="#10b981"/>}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          {lokasyonlar.length>0&&(
            <button className="btn btn-primary" onClick={handleAta} disabled={saving||!secili.size}
              style={{background:'#10b981',borderColor:'#10b981'}}>
              {saving?<div className="spinner"/>:<MapPin size={14}/>}
              {secili.size>0?`${secili.size} Lokasyona Ata`:'Lokasyon Seç'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ANA COMPONENT
══════════════════════════════════════════════════════════════ */
export default function Checklistler() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalSorular, setModalSorular] = useState([]);
  const [detayId, setDetayId] = useState(null);
  const [detay, setDetay] = useState(null);
  const [form, setForm] = useState({ baslik:'', kurum_id:'', sorular:[] });
  const [saving, setSaving] = useState(false);
  const [yeniSoru, setYeniSoru] = useState({...BOSH_SORU});
  const [soruEkleniyor, setSoruEkleniyor] = useState(false);
  const [editSoru, setEditSoru] = useState(null);
  const [ataModal, setAtaModal] = useState(null);
  const [onizleme, setOnizleme] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get('/checklistler')];
      if (user.rol==='superadmin') reqs.push(api.get('/kurumlar'));
      const [c,k] = await Promise.all(reqs);
      setList(c.data); if(k) setKurumlar(k.data);
    } catch { toast.error('Yüklenemedi'); } finally { setLoading(false); }
  };

  const loadDetay = async id => {
    try { const {data}=await api.get(`/checklistler/${id}`); setDetay(data); }
    catch { toast.error('Detay yüklenemedi'); }
  };

  useEffect(()=>{load();},[]);
  useEffect(()=>{ if(detayId) loadDetay(detayId); else setDetay(null); },[detayId]);

  const openEkle = () => {
    setForm({baslik:'',kurum_id:user.rol==='superadmin'?'':user.kurum_id,sorular:[]});
    setYeniSoru({...BOSH_SORU}); setModalSorular([]); setEditSoru(null); setModal('ekle');
  };
  const openDuzenle = async c => {
    setForm({baslik:c.baslik,kurum_id:c.kurum_id,sorular:[]});
    setYeniSoru({...BOSH_SORU}); setEditSoru(null); setModal(c);
    try { const {data}=await api.get(`/checklistler/${c.id}`); setModalSorular(data.sorular||[]); }
    catch { setModalSorular([]); }
  };

  const addSoruToForm = () => {
    if (!yeniSoru.soru_metni.trim()) return toast.error('Madde metni gerekli');
    if (yeniSoru.soru_tipi==='cok_secenekli'&&(yeniSoru.secenekler||[]).filter(Boolean).length<2) return toast.error('En az 2 seçenek');
    setForm(f=>({...f,sorular:[...f.sorular,{...yeniSoru}]})); setYeniSoru({...BOSH_SORU});
  };
  const removeSoruFromForm = (_,i) => setForm(f=>({...f,sorular:f.sorular.filter((_,j)=>j!==i)}));
  const editSoruInForm = (s,i) => setEditSoru({soru:{...s},index:i,mode:'form'});
  const saveEditForm = () => {
    if (!editSoru.soru.soru_metni.trim()) return toast.error('Madde metni gerekli');
    setForm(f=>({...f,sorular:f.sorular.map((s,i)=>i===editSoru.index?{...editSoru.soru}:s)})); setEditSoru(null);
  };
  const handleSoruEkleDB = async (clId) => {
    if (!yeniSoru.soru_metni.trim()) return toast.error('Madde metni gerekli');
    if (yeniSoru.soru_tipi==='cok_secenekli'&&(yeniSoru.secenekler||[]).filter(Boolean).length<2) return toast.error('En az 2 seçenek');
    setSoruEkleniyor(true);
    try {
      await api.post(`/checklistler/${clId}/sorular`,{...yeniSoru,secenekler:(yeniSoru.secenekler||[]).filter(Boolean)});
      toast.success('Madde eklendi'); setYeniSoru({...BOSH_SORU});
      const {data}=await api.get(`/checklistler/${clId}`); setModalSorular(data.sorular||[]);
      if(detayId===clId) loadDetay(clId);
    } catch { toast.error('Hata'); } finally { setSoruEkleniyor(false); }
  };
  const handleSoruSilDB = async (clId,soruId) => {
    if (!window.confirm('Maddeyi silmek istiyor musunuz?')) return;
    try { await api.delete(`/checklistler/sorular/${soruId}`); setModalSorular(prev=>prev.filter(s=>s.id!==soruId)); if(detayId===clId) loadDetay(clId); }
    catch { toast.error('Silinemedi'); }
  };
  const handleSoruGuncelleDB = async (clId,soruId,veri) => {
    try {
      await api.put(`/checklistler/sorular/${soruId}`,{...veri,secenekler:(veri.secenekler||[]).filter(Boolean)});
      toast.success('Madde güncellendi'); setEditSoru(null);
      const {data}=await api.get(`/checklistler/${clId}`); setModalSorular(data.sorular||[]);
      if(detayId===clId) loadDetay(clId);
    } catch { toast.error('Güncellenemedi'); }
  };
  const editSoruDB = (s) => setEditSoru({soru:{soru_metni:s.soru_metni,soru_tipi:s.soru_tipi,zorunlu:s.zorunlu,secenekler:s.secenekler||[],id:s.id},mode:'db'});

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if(modal==='ekle') { await api.post('/checklistler',{...form,sorular:form.sorular.map(s=>({...s,secenekler:(s.secenekler||[]).filter(Boolean)}))}); toast.success('Checklist oluşturuldu'); }
      else { await api.put(`/checklistler/${modal.id}`,{baslik:form.baslik,aktif:true}); toast.success('Checklist güncellendi'); }
      setModal(null); load();
    } catch(err) { toast.error(err.response?.data?.error||'Hata'); } finally { setSaving(false); }
  };
  const handleDelete = async c => {
    if(!window.confirm(`"${c.baslik}" checklistini silmek istiyor musunuz?`)) return;
    try { await api.delete(`/checklistler/${c.id}`); toast.success('Silindi'); load(); if(detayId===c.id) setDetayId(null); }
    catch(err) { toast.error(err.response?.data?.error||'Silinemedi'); }
  };

  const renderSorular = (sorular, clId, mode) => (
    <>
      {sorular.map((s,i)=>(
        <SoruSatir key={s.id||i} s={s} i={i}
          onSil={mode==='form'?(_,idx)=>removeSoruFromForm(_,idx):(s)=>handleSoruSilDB(clId,s.id)}
          onEdit={mode==='form'?editSoruInForm:(s)=>editSoruDB(s)}/>
      ))}
      {!sorular.length&&<p style={{color:'var(--gray-400)',fontSize:13,marginBottom:10}}>Henüz madde yok</p>}
      {editSoru&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:'var(--navy)'}}>✏️ Maddeyi Düzenle</div>
          <SoruForm value={editSoru.soru} onChange={s=>setEditSoru(prev=>({...prev,soru:s}))}
            onSubmit={mode==='form'?saveEditForm:()=>handleSoruGuncelleDB(clId,editSoru.soru.id,editSoru.soru)}
            label="Güncelle" onCancel={()=>setEditSoru(null)}/>
        </div>
      )}
      {!editSoru&&<SoruForm value={yeniSoru} onChange={setYeniSoru}
        onSubmit={mode==='form'?addSoruToForm:()=>handleSoruEkleDB(clId)} loading={soruEkleniyor}/>}
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Checklist Şablonları</h1>
          <p className="page-subtitle">{list.length} şablon mevcut</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}><Plus size={16}/> Yeni Checklist</button>
      </div>

      <div className={detayId?'chart-grid':''} style={{display:'grid',gap:20}}>
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Checklist Adı</th>
                {user.rol==='superadmin'&&<th>Kurum</th>}
                <th>Madde</th><th>Durum</th><th></th>
              </tr></thead>
              <tbody>
                {list.map(c=>(
                  <tr key={c.id} style={{cursor:'pointer',background:detayId===c.id?'#f0fdf4':''}}>
                    <td onClick={()=>setDetayId(detayId===c.id?null:c.id)}>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div style={{width:32,height:32,borderRadius:9,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          <CheckSquare size={15} color="#16a34a"/>
                        </div>
                        <span style={{fontWeight:600}}>{c.baslik}</span>
                      </div>
                    </td>
                    {user.rol==='superadmin'&&<td onClick={()=>setDetayId(detayId===c.id?null:c.id)}>{c.kurum_adi||'—'}</td>}
                    <td onClick={()=>setDetayId(detayId===c.id?null:c.id)}><span className="badge badge-green">{c.soru_sayisi}</span></td>
                    <td><span className={`badge ${c.aktif?'badge-green':'badge-gray'}`}>{c.aktif?'Aktif':'Pasif'}</span></td>
                    <td>
                      <div style={{display:'flex',gap:5}}>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Önizle"
                          onClick={async ()=>{ try{ const {data}=await api.get(`/checklistler/${c.id}`); setOnizleme({...c,sorular:data.sorular||[]}); }catch{} }}>
                          <Eye size={13}/>
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setAtaModal(c)} title="Lokasyona Ata">
                          <MapPin size={13}/> Ata
                        </button>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={()=>openDuzenle(c)}><Pencil size={13}/></button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={()=>handleDelete(c)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length&&!loading&&<tr><td colSpan={5} style={{textAlign:'center',padding:40,color:'var(--gray-400)'}}>Henüz checklist yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {detayId&&detay&&(
          <div className="card" style={{padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <h3 style={{margin:0,fontSize:15,fontWeight:700}}>{detay.checklist?.baslik}</h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setDetayId(null)}><X size={15}/></button>
            </div>
            <h4 style={{fontSize:13,fontWeight:600,color:'var(--gray-600)',marginBottom:10}}>Maddeler ({detay.sorular?.length})</h4>
            {renderSorular(detay.sorular||[],detayId,'db')}
          </div>
        )}
      </div>

      {modal&&(
        <Modal title={modal==='ekle'?'Yeni Checklist Şablonu':`Düzenle: ${modal.baslik}`} onClose={()=>setModal(null)} wide>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              {user.rol==='superadmin'&&modal==='ekle'&&(
                <div className="input-group">
                  <label className="input-label">Kurum *</label>
                  <select className="input" required value={form.kurum_id} onChange={e=>setForm({...form,kurum_id:e.target.value})}>
                    <option value="">Seçin...</option>
                    {kurumlar.map(k=><option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              )}
              <div className="input-group">
                <label className="input-label">Checklist Adı *</label>
                <input className="input" required value={form.baslik} onChange={e=>setForm({...form,baslik:e.target.value})} placeholder="Günlük Temizlik Kontrol Listesi"/>
              </div>
              <div>
                <div style={{fontWeight:600,fontSize:14,marginBottom:8,color:'var(--navy)'}}>
                  Maddeler ({modal==='ekle'?form.sorular.length:modalSorular.length})
                </div>
                {modal==='ekle'?renderSorular(form.sorular,null,'form'):renderSorular(modalSorular,modal.id,'db')}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving?<div className="spinner"/>:null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}

      {ataModal&&(
        <LokasyonAtaModal
          sablon={ataModal}
          onClose={()=>setAtaModal(null)}
          onAtandi={load}
        />
      )}
      {onizleme&&(
        <OnizlemeModal
          tip="checklist"
          sablon={onizleme}
          onClose={()=>setOnizleme(null)}
        />
      )}
    </div>
  );
}